import { useEffect, useState } from "react";
import {
  FiSearch,
  FiTrash,
  FiFilter,
  FiGrid,
  FiList,
  FiX,
  FiEdit2,
  FiPlus,
} from "react-icons/fi";
import {
  getContainers,
  createContainer,
  updateContainer,
  deleteContainer,
} from "../services/containerApi";
import AddContainerModal from "../components/AddContainerModal";
import ContainerCard from "../components/ContainerCard";

const StatusBadge = ({ status }) => {
  const styles = {
    "In Transit": "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
    "Arrived at Port": "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300",
    "Customs Cleared": "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    Delivered: "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300",
    "Departed from Origin Port":
      "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  };

  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-medium ${
        styles[status] || styles["In Transit"]
      }`}
    >
      {status || "In Transit"}
    </span>
  );
};

export default function Containers() {
  const [containers, setContainers] = useState([]);
  const [filteredContainers, setFilteredContainers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingContainer, setEditingContainer] = useState(null);
  const [viewMode, setViewMode] = useState("table");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    fromDate: null,
    toDate: null,
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchData = async () => {
    try {
      const res = await getContainers();
      setContainers(res.data);
    } catch (error) {
      console.error("Error fetching containers:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = [...containers];

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter((c) => {
        return (
          (c.bookingBLNo || "").toLowerCase().includes(q) ||
          (c.containerNumber || "").toLowerCase().includes(q) ||
          (c.vesselName || "").toLowerCase().includes(q) ||
          (c.origin || "").toLowerCase().includes(q) ||
          (c.destination || "").toLowerCase().includes(q) ||
          (c.shipper || "").toLowerCase().includes(q) ||
          (c.consignee || "").toLowerCase().includes(q)
        );
      });
    }

    if (filters.status) {
      filtered = filtered.filter((c) => c.status === filters.status);
    }

    if (filters.fromDate) {
      filtered = filtered.filter((c) => new Date(c.date) >= filters.fromDate);
    }

    if (filters.toDate) {
      filtered = filtered.filter((c) => new Date(c.date) <= filters.toDate);
    }

    setFilteredContainers(filtered);
  }, [containers, searchTerm, filters]);

  const handleSave = async (data) => {
    try {
      if (editingContainer) {
        await updateContainer(editingContainer.id, data);
      } else {
        await createContainer(data);
      }

      await fetchData();
      setEditingContainer(null);
      setShowModal(false);
    } catch (error) {
      console.error("Error saving container:", error);
      alert(error.response?.data?.message || "Error saving container");
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this container? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await deleteContainer(id);
      await fetchData();
    } catch (error) {
      console.error("Error deleting container:", error);
      alert("Error deleting container");
    }
  };

  const handleEdit = (container) => {
    setEditingContainer(container);
    setShowModal(true);
  };

  const handleView = (container) => {
    alert(`View details for ${container.bookingBLNo || container.containerNumber || container.id}`);
  };

  const clearFilters = () => {
    setFilters({ status: "", fromDate: null, toDate: null });
    setSearchTerm("");
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-GB");
  };

  const statusOptions = [
    "In Transit",
    "Arrived at Port",
    "Customs Cleared",
    "Delivered",
    "Departed from Origin Port",
  ];

  const dateToInputValue = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  const baseInput =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500";
  const card =
    "rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900";

  return (
    <div className="space-y-6 p-6 text-slate-900 dark:text-slate-100">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
            Containers
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Manage and track all shipping containers
          </p>
        </div>

        <button
          onClick={() => {
            setEditingContainer(null);
            setShowModal(true);
          }}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-white shadow-sm transition hover:bg-blue-700"
        >
          <FiPlus size={18} />
          Add Container
        </button>
      </div>

      <div className={`${card} p-4`}>
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="relative flex-1">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search by booking/BL no, container number, vessel, origin, or destination..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-4 text-slate-900 outline-none transition focus:border-transparent focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500"
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 rounded-lg border px-4 py-2 transition ${
                showFilters
                  ? "border-blue-300 bg-blue-50 text-blue-600 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-300"
                  : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
              }`}
            >
              <FiFilter /> Filters
            </button>

            <div className="flex overflow-hidden rounded-lg border border-slate-300 dark:border-slate-700">
              <button
                onClick={() => setViewMode("table")}
                className={`px-3 py-2 transition ${
                  viewMode === "table"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
                }`}
              >
                <FiList />
              </button>
              <button
                onClick={() => setViewMode("grid")}
                className={`px-3 py-2 transition ${
                  viewMode === "grid"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
                }`}
              >
                <FiGrid />
              </button>
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 grid grid-cols-1 gap-4 border-t border-slate-200 pt-4 dark:border-slate-800 sm:grid-cols-3">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className={baseInput}
              >
                <option value="">All Status</option>
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                From Date
              </label>
              <input
                type="date"
                value={dateToInputValue(filters.fromDate)}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    fromDate: e.target.value ? new Date(e.target.value) : null,
                  })
                }
                className={baseInput}
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                To Date
              </label>
              <input
                type="date"
                value={dateToInputValue(filters.toDate)}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    toDate: e.target.value ? new Date(e.target.value) : null,
                  })
                }
                className={baseInput}
              />
            </div>

            {(filters.status || filters.fromDate || filters.toDate || searchTerm) && (
              <div className="flex justify-end sm:col-span-3">
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-2 text-red-600 transition hover:text-red-700 dark:text-red-300 dark:hover:text-red-200"
                >
                  <FiX /> Clear All Filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Showing{" "}
          <span className="font-semibold text-slate-900 dark:text-slate-100">
            {filteredContainers.length}
          </span>{" "}
          of{" "}
          <span className="font-semibold text-slate-900 dark:text-slate-100">
            {containers.length}
          </span>{" "}
          containers
        </p>
      </div>

      {viewMode === "table" && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Booking/BL No
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Container No.
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Vessel Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Origin
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Destination
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Delivery Booked On
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-300">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {filteredContainers.map((container) => (
                  <tr
                    key={container.id}
                    className="transition hover:bg-slate-50 dark:hover:bg-slate-800/60"
                  >
                    <td className="px-6 py-4 font-medium text-slate-900 dark:text-slate-100">
                      {container.bookingBLNo || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                      {formatDate(container.date)}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                      {container.containerNumber || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                      {container.vesselName || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                      {container.origin || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                      {container.destination || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                      {container.containerType || "N/A"}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-300">
                      {formatDate(container.deliveryBookedOn)}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={container.status} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleView(container)}
                          className="rounded-lg p-2 text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                          title="View Details"
                        >
                          <FiSearch size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(container)}
                          className="rounded-lg p-2 text-blue-600 transition hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950"
                          title="Edit"
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(container.id)}
                          className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950"
                          title="Delete"
                        >
                          <FiTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredContainers.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-slate-500 dark:text-slate-400">No containers found</p>
              <button
                onClick={() => {
                  setEditingContainer(null);
                  setShowModal(true);
                }}
                className="mt-2 text-blue-600 transition hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200"
              >
                Add your first container
              </button>
            </div>
          )}
        </div>
      )}

      {viewMode === "grid" && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredContainers.map((container) => (
            <ContainerCard
              key={container.id}
              container={container}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}

          {filteredContainers.length === 0 && (
            <div className="col-span-full py-12 text-center">
              <p className="text-slate-500 dark:text-slate-400">No containers found</p>
            </div>
          )}
        </div>
      )}

      <AddContainerModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingContainer(null);
        }}
        onSave={handleSave}
        editingContainer={editingContainer}
      />
    </div>
  );
}